import type { DepthAvailability, DepthFailureCause } from "@/lib/measurement/depthAvailability";

export const getDepthToggleLabel = (isVisible: boolean): string => (isVisible ? "Hide Depth" : "View Depth");

export type DepthAvailabilityNotice = {
  text: string;
  tone: "info" | "warn" | "error";
  retryable: boolean;
};

/**
 * What the editor should tell the user about this capture's depth, or null when
 * there is nothing worth saying (depth is ready, or nothing is being edited).
 *
 * Each message names the consequence for editing rather than the mechanism, and
 * only the states a retry could actually change are marked retryable.
 */
export function describeDepthAvailability(
  availability: DepthAvailability | null | undefined
): DepthAvailabilityNotice | null {
  if (!availability) return null;

  switch (availability.status) {
    case "ready":
      return null;
    case "metadata-loading":
      return { text: "Loading capture info…", tone: "info", retryable: false };
    case "loading":
      // Drawing is never blocked on depth: a box drawn now is measured on arrival.
      return { text: "Loading depth… New boxes will be measured when it's ready.", tone: "info", retryable: false };
    case "absent":
      // Not a failure: a depth-less capture is still fully editable, just without
      // physical distances. Nothing to retry.
      return {
        text: "No depth map — editing landmarks in 2D. Physical distances are unavailable, so lines show dashed.",
        tone: "warn",
        retryable: false,
      };
    case "metadata-error":
      return { text: "Couldn't load capture info", tone: "error", retryable: true };
    case "retryable-error":
      return {
        text:
          availability.cause === "entry-depth-path-missing"
            ? "Depth file not recorded for this entry"
            : "Depth failed to load",
        tone: "error",
        retryable: true,
      };
    case "terminal-error":
      return { text: TERMINAL_DEPTH_FAILURE_TEXT[availability.cause], tone: "error", retryable: false };
  }
}

/** Terminal copy per cause. `Record` over the union so a new cause without copy
 *  is a compile error instead of silently rendering a generic message. */
const TERMINAL_DEPTH_FAILURE_TEXT: Record<DepthFailureCause, string> = {
  "entry-depth-path-missing": "Depth file missing — measurement editing locked",
  "object-missing": "Depth file missing — measurement editing locked",
  "entry-depth-path-orphaned": "Depth records inconsistent — measurement editing locked",
  "metadata-fetch-failed": "Couldn't load capture info",
  "fetch-failed": "Depth unusable — measurement editing locked",
  "decode-failed": "Depth unusable — measurement editing locked",
  "missing-descriptor": "Depth unusable — measurement editing locked",
  misaligned: "Depth unusable — measurement editing locked",
  "dimension-mismatch": "Depth unusable — measurement editing locked",
  "frame-invalid": "Depth unusable — measurement editing locked",
};
